import i18next from 'i18next';
import dayjs from 'dayjs';
// Formatting and classification shared by the report page sections (ported from the ReportPage design)

// The page's language. English keeps the formats the page has always used ("12,500", "1 Apr 2021"); every other
// language gets its own from the browser (decision 19 of the report translation plan, SC-QA-119)
const pageLang = () => (i18next.isInitialized && i18next.resolvedLanguage) || 'en';
const isEnglish = () => pageLang() === 'en';

export const fmt = n => Number(n).toLocaleString(isEnglish() ? 'en-US' : pageLang());

// an unreadable date (the parser's free text, e.g. "12 months") shows as dayjs shows it in English, never throws:
// Intl.DateTimeFormat throws a RangeError on an Invalid Date, which would blank the page
const intlDate = (value, options) => {
    const date = dayjs(value);
    return date.isValid() ? new Intl.DateTimeFormat(pageLang(), options).format(date.toDate()) : date.format();
};

// "1 Apr 2021"; "1. Apr. 2021" in German, "1 квіт. 2021 р." in Ukrainian
export const formatDay = value =>
    isEnglish()
        ? dayjs(value).format('D MMM YYYY')
        : intlDate(value, { day: 'numeric', month: 'short', year: 'numeric' });

// "Apr 2021"
export const formatMonth = value =>
    isEnglish() ? dayjs(value).format('MMM YYYY') : intlDate(value, { month: 'short', year: 'numeric' });

// "1 Apr 2021, 14:05", or "1 Apr, 14:05" without the year
export const formatDayTime = (value, { year = true } = {}) => {
    if (isEnglish()) return dayjs(value).format(year ? 'D MMM YYYY, HH:mm' : 'D MMM, HH:mm');
    return intlDate(value, {
        day: 'numeric',
        month: 'short',
        ...(year ? { year: 'numeric' } : {}),
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
};

// English country name → its ISO code, from the browser's own list (the parser writes "Germany", not "DE")
let countryCodes = null;
const codeOfCountry = name => {
    if (!countryCodes) {
        countryCodes = new Map();
        try {
            const english = new Intl.DisplayNames(['en'], { type: 'region' });
            const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            for (const a of letters) {
                for (const b of letters) {
                    const name = english.of(a + b);
                    if (name && name !== a + b) countryCodes.set(name.toLowerCase(), a + b);
                }
            }
        } catch {
            // a browser without Intl.DisplayNames keeps the names as written
        }
    }
    return countryCodes.get(String(name).trim().toLowerCase()) || null;
};

// A country (code or English name) in the page's language: "Deutschland" for "Germany" on a German page. Anything
// not recognised is shown as written
export const countryName = value => {
    const text = String(value || '').trim();
    if (!text) return '';
    const code = /^[a-z]{2}$/i.test(text) ? text.toUpperCase() : codeOfCountry(text);
    if (!code) return text;
    try {
        return new Intl.DisplayNames([pageLang()], { type: 'region' }).of(code) || text;
    } catch {
        return text;
    }
};

// The smallest "round" axis maximum at or above v (1, 2, 2.5, 4, 5 or 10 times a power of ten)
export const niceCeil = v => {
    if (!(v > 0)) return 1000;
    const power = Math.pow(10, Math.floor(Math.log10(v)));
    const step = [1, 2, 2.5, 4, 5, 10].find(s => s * power >= v);
    return (step || 10) * power;
};

// English units, used until the reportPage translations are loaded (and in unit tests)
const UNIT_FALLBACK = { km: 'km', kw: 'kW', hp: 'hp', cm3: 'cm³', um: 'µm', l: 'l' };

// A unit in the UI language (Ukrainian and Bulgarian write "км", "кВт"...): read from the i18n instance so the
// view helpers do not need `t` passed through
export const unit = name =>
    i18next.isInitialized
        ? i18next.t(`reportPage:units.${name}`, { defaultValue: UNIT_FALLBACK[name] })
        : UNIT_FALLBACK[name];

// "12,500 km", "135 kW", "1,598 cm³"
export const withUnit = (value, name) => `${value} ${unit(name)}`;

// visit tags are English ids (they pick the style and the timeline node); this is the translated label
const TAG_KEYS = {
    Maintenance: 'maintenance',
    Service: 'service',
    Repair: 'repair',
    'Bodywork / paint': 'bodywork',
    Tyres: 'tyres',
    Inspection: 'inspection',
    'Key read': 'keyRead',
    Recall: 'recall',
    Warranty: 'warranty',
    Complaint: 'complaint',
};
export const tagLabel = (tag, t) => (TAG_KEYS[tag] ? t(`labels.tags.${TAG_KEYS[tag]}`, { defaultValue: tag }) : tag);

// Design-system tag style for a visit tag
export const tagStyle = tag => {
    if (tag === 'Maintenance' || tag === 'Service') return 'success';
    if (tag === 'Bodywork / paint' || tag === 'Repair' || tag === 'Complaint') return 'warning';
    if (tag === 'Recall') return 'error';
    return 'neutral';
};

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Year and month (0-11) of a visit or event: from its real date (`sortDate`) when it has one, so a date shown in any
// language still places it; otherwise read from an English display date
export const monthYearOf = item => {
    const real = item && item.sortDate ? dayjs(item.sortDate) : null;
    if (real && real.isValid()) return { y: real.year(), m: real.month() };
    return parseMonthYear(item && item.date);
};

// Year and month (0-11) of a display date such as "1 Apr 2021" or "Sep 2020"; month 0 when it has none
export const parseMonthYear = text => {
    const value = String(text || '');
    const year = value.match(/(\d{4})/);
    const month = value.toLowerCase().match(/[a-z]{3}/);
    const monthIndex = month ? MONTHS.indexOf(month[0]) : -1;

    return { y: year ? Number(year[1]) : null, m: monthIndex < 0 ? 0 : monthIndex };
};

export const plural = (n, one, many) => (n === 1 ? one : many);

// chevron points for an open (up) or closed (down) toggle
export const chevron = open => (open ? '208 160 128 80 48 160' : '208 96 128 176 48 96');

// Areas of the car map, with their position on the 800 x 300 side view
export const ZONES = [
    { k: 'front', name: 'Front body', x: 62, y: 192 },
    { k: 'engine', name: 'Engine bay', x: 175, y: 140 },
    { k: 'glass', name: 'Windscreen and glass', x: 300, y: 104 },
    { k: 'wheels', name: 'Tyres and wheels', x: 175, y: 222 },
    { k: 'cabin', name: 'Interior and climate', x: 530, y: 110 },
    { k: 'suspension', name: 'Suspension', x: 400, y: 238 },
    { k: 'brakes', name: 'Brakes and clutch', x: 618, y: 222 },
    { k: 'rear', name: 'Rear body', x: 712, y: 124 },
];

const ZONE_PATTERNS = [
    ['glass', /glass|windscreen|windshield|rain\/light/],
    ['rear', /tailgate|rear body|rear bumper|boot lid|rear wing|rear fender/],
    ['front', /bumper|grille|spoiler|bonnet|hood|wing|fender|headlight|body/],
    ['wheels', /tyre|tire|wheel/],
    ['brakes', /brake|clutch/],
    ['suspension', /suspension|spring|shock|axle|stabili/],
    ['cabin', /climate|a\/c|condenser|air con|pollen|interior|seat|wiper/],
    ['engine', /engine|oil|filter|spark|ignition|fluid|coolant|cable|belt|exhaust/],
];

// The car-map area a replaced part belongs to: its own zone, its group's, or guessed from the names
export const zoneOf = (group, item) => {
    if (item.zone) return item.zone;
    if (group.zone) return group.zone;
    const text = `${item.component} ${group.name}`.toLowerCase();
    const match = ZONE_PATTERNS.find(([, pattern]) => pattern.test(text));
    return match ? match[0] : 'cabin';
};
